import React, { useEffect, useMemo, useState } from "react";
import html2canvas from "html2canvas";
import { API_BASE, uploadAnnouncementImage } from "../services/apiClient";
import "./ExcelUploadForm.css";
import { formatDate } from "../utils/dateFormat";
import { fitPrintedField } from "../forms/shared/PrintField";
import { PageNav, PageSummary, usePaging } from "./admin/Pager";

// html2canvas lays the cloned page out at this width (desktop), whatever the device
const CAPTURE_WINDOW_WIDTH = 1280;

function displayDate(value) {
  return value ? formatDate(String(value).slice(0, 10)) : "-";
}

function itemKey(item) {
  return item.status === "pending" || item.status === "error"
    ? `pending:${item.tempId}`
    : `saved:${item.id}`;
}

function savePreviewItemToSession(item) {
  if (!item.tempId) return;
  sessionStorage.setItem(`excelPreview:${item.tempId}`, JSON.stringify(item));
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function waitForElement(getDoc, selector, timeoutMs = 20000) {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    const doc = getDoc();
    const element = doc.querySelector(selector);
    if (element) return element;
    await wait(150);
  }
  throw new Error(`Timed out waiting for ${selector}`);
}

function waitForFrameLoad(iframe, timeoutMs = 20000) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error("Timed out waiting for imported form frame"));
    }, timeoutMs);

    iframe.onload = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve();
    };
  });
}

function getCapturedControlText(control) {
  if (control.tagName === "SELECT") {
    return control.options[control.selectedIndex]?.text || control.value || "\u00a0";
  }
  return control.value || control.getAttribute("placeholder") || "\u00a0";
}

function replaceControlsForCapture(clonedRoot, clonedDocument) {
  const win = clonedDocument.defaultView;
  if (!win) return;

  clonedRoot
    .querySelectorAll('input:not([type="hidden"]), textarea, select')
    .forEach((control) => {
      const style = win.getComputedStyle(control);
      const rect = control.getBoundingClientRect();
      const span = clonedDocument.createElement("span");
      span.className = control.className;
      span.classList.add("mfw-capture-text");
      span.textContent = getCapturedControlText(control);

      [
        "width",
        "minWidth",
        "maxWidth",
        "margin",
        "boxSizing",
        "fontFamily",
        "fontSize",
        "fontWeight",
        "fontStyle",
        "letterSpacing",
        "color",
        "verticalAlign",
        "whiteSpace",
        "background",
        "textDecoration",
        "textAlign",
      ].forEach((property) => {
        span.style[property] = style[property];
      });

      span.style.display = "inline-block";
      span.style.border = "0";
      span.style.borderBottom = "0";
      span.style.outline = "0";
      span.style.boxShadow = "none";
      span.style.height = "auto";
      span.style.minHeight = "0";
      span.style.lineHeight = "1";
      span.style.paddingTop = "0";
      span.style.paddingBottom = "0";
      span.style.paddingLeft = style.paddingLeft;
      span.style.paddingRight = style.paddingRight;
      span.style.overflow = "visible";
      // keep at least the field's space, but grow / wrap for longer text instead of running over the next words
      span.style.whiteSpace = "pre-wrap";
      span.style.maxWidth = "100%";

      if (control.dataset.printFit) {
        span.style.width = "auto";
        span.style.minWidth = "0";
      } else if (Number.isFinite(rect.width) && rect.width > 0) {
        span.style.width = "auto";
        span.style.minWidth = `${rect.width}px`;
      }

      control.replaceWith(span);
      // same sizing rule as the form's own capture (see fitPrintedField in PrintField.js)
      if (!control.dataset.printFit) fitPrintedField(span, rect.width);
    });
}

// same rule as the form's own capture: an empty address line 2 is left out, a filled one takes only its text width
function fitOptionalRows(clonedRoot) {
  clonedRoot.querySelectorAll(".inv-place-row").forEach((row) => {
    const control = row.querySelector("input, textarea");
    if (!control) return;
    if (!String(control.value || "").trim()) row.remove();
    else control.dataset.printFit = "1";
  });
}

// Render an Excel-imported item in its form (hidden iframe, desktop width) and capture the paper as an A4 PNG blob.
// Also used by "สร้างภาพใหม่ (A4)" in the admin announcement list to re-capture old squeezed images.
export async function captureImportedItemImage(item) {
  const route = item.route || "/FinancialApproval";
  const iframe = document.createElement("iframe");
  iframe.className = "euf-capture-frame";
  iframe.setAttribute("aria-hidden", "true");
  const frameReady = waitForFrameLoad(iframe);
  document.body.appendChild(iframe);
  iframe.src = `${window.location.origin}/#${route}?importId=${encodeURIComponent(item.id)}`;

  try {
    await frameReady;
    const getFrameDocument = () => {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) throw new Error("Cannot access capture iframe");
      return doc;
    };

    const paper = await waitForElement(getFrameDocument, ".mfw-preview-wrapper .inv-paper");
    await wait(700);
    if (iframe.contentDocument?.fonts?.ready) await iframe.contentDocument.fonts.ready;
    await wait(150);

    const canvas = await html2canvas(paper, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      // always lay the paper out as a desktop A4 page — on a phone / narrow window the paper used to
      // reflow to the screen width, so the saved image came out squeezed (e.g. 558px wide instead of 210mm)
      windowWidth: CAPTURE_WINDOW_WIDTH,
      onclone: (clonedDocument, clonedElement) => {
        clonedElement.style.width = "210mm";
        clonedElement.style.maxWidth = "none";
        clonedElement.style.minWidth = "210mm";
        // print look (white paper, black text) — the same as a capture from the form page itself
        clonedElement.closest(".mfw-preview-wrapper")?.classList.add("mfw-preview-wrapper-clean");
        clonedElement.style.border = "0";
        clonedElement.style.outline = "0";
        clonedElement.style.boxShadow = "none";
        fitOptionalRows(clonedElement);
        replaceControlsForCapture(clonedElement, clonedDocument);
      },
    });

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
    if (!blob) throw new Error(`Cannot create image for imported item ${item.id}`);
    return blob;
  } finally {
    iframe.remove();
  }
}

async function captureImportedItemAsAnnouncement(item) {
  const issueDate = item.issueDate || item.formData?.issueDate;
  if (!issueDate) throw new Error(`Missing issueDate for imported item ${item.id}`);

  const blob = await captureImportedItemImage(item);
  return uploadAnnouncementImage({
    fileOrBlob: blob,
    filename: `imported-announcement-${item.id}.png`,
    issueDate,
  });
}

function ExcelUploadForm() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [pendingItems, setPendingItems] = useState([]);
  const [savedItems, setSavedItems] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [deletingSelected, setDeletingSelected] = useState(false);
  const [captureStatus, setCaptureStatus] = useState("");
  const [capturingImages, setCapturingImages] = useState(false);

  const items = useMemo(() => pendingItems, [pendingItems]);
  const paging = usePaging(items, "mng_excel_page_size", "");
  const saveablePendingItems = pendingItems.filter((item) => !item.errors?.length);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!/\.xlsx$/i.test(selectedFile.name)) {
      window.alert("กรุณาเลือกไฟล์ Excel .xlsx เท่านั้น (ไฟล์ .xls รุ่นเก่า ให้เปิดแล้วบันทึกเป็น .xlsx ก่อน)");
      e.target.value = "";
      return;
    }

    setFile(selectedFile);
    setUploadResult(null);
  };

  const loadList = async () => {
    try {
      setLoadingList(true);
      const res = await fetch(`${API_BASE}/api/excel/list`);
      const data = await res.json();
      if (data.success) {
        const nextSavedItems = (data.items || []).map((item) => ({
          ...item,
          status: item.status || "saved",
        }));
        setSavedItems(nextSavedItems);
        setSelectedKeys((prev) => {
          const valid = new Set([...pendingItems.map(itemKey), ...nextSavedItems.map(itemKey)]);
          return prev.filter((key) => valid.has(key));
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingList(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      // ใช้ fetch() แทน window.location.href เพื่อให้ CRA proxy forward ได้
      // และไม่ navigate หน้าออกไป
      const res = await fetch(`${API_BASE}/api/excel/template/download`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'announcement-import-template.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      window.alert('ดาวน์โหลด template ไม่สำเร็จ: ' + err.message);
    }
  };

  const handlePreviewUpload = async () => {
    if (!file) {
      window.alert("กรุณาเลือกไฟล์ Excel");
      return;
    }

    try {
      setUploading(true);
      const fd = new FormData();
      fd.append("file", file);

      const res = await fetch(`${API_BASE}/api/excel/preview`, {
        method: "POST",
        body: fd,
      });
      const data = await res.json();
      const result = res.ok ? data : { success: false, message: data.message || `Preview failed: HTTP ${res.status}` };

      setUploadResult(result);

      if (result.success) {
        const nextPendingItems = result.items || [];
        setPendingItems(nextPendingItems);
        setSelectedKeys([]);
        setFile(null);
        const input = document.getElementById("excel-file-input");
        if (input) input.value = "";
      }
    } catch (err) {
      setUploadResult({ success: false, message: err.message });
    } finally {
      setUploading(false);
    }
  };

  const openTemplate = (item) => {
    const route = item.route || "/FinancialApproval";

    if (item.status === "pending" || item.status === "error") {
      savePreviewItemToSession(item);
      window.open(`${window.location.origin}/#${route}?previewId=${item.tempId}`, "_blank");
      return;
    }

    const query = new URLSearchParams({ importId: item.id });
    window.open(`${window.location.origin}/#${route}?${query.toString()}`, "_blank");
  };

  const deleteById = async (id) => {
    const res = await fetch(`${API_BASE}/api/excel/${id}`, { method: "DELETE" });
    const data = await res.json();
    if (!data.success) throw new Error(data.message || `Delete failed: ${id}`);
  };

  const handleDelete = async (item) => {
    if (!window.confirm("ยืนยันลบรายการนี้?")) return;

    if (item.status === "pending" || item.status === "error") {
      setPendingItems((prev) => prev.filter((pending) => pending.tempId !== item.tempId));
      setSelectedKeys((prev) => prev.filter((key) => key !== itemKey(item)));
      sessionStorage.removeItem(`excelPreview:${item.tempId}`);
      return;
    }

    try {
      await deleteById(item.id);
      setSelectedKeys((prev) => prev.filter((key) => key !== itemKey(item)));
      await loadList();
    } catch (err) {
      console.error(err);
      window.alert(err.message || "ลบรายการไม่สำเร็จ");
    }
  };

  // the header box ticks / unticks the rows on this page only, so "Delete selected" never removes rows the admin cannot see
  const toggleSelectAll = (checked) => {
    const keys = paging.shown.map(itemKey);
    setSelectedKeys((prev) => (checked ? [...new Set([...prev, ...keys])] : prev.filter((k) => !keys.includes(k))));
  };

  const toggleSelectItem = (item, checked) => {
    const key = itemKey(item);
    setSelectedKeys((prev) =>
      checked ? [...new Set([...prev, key])] : prev.filter((selectedKey) => selectedKey !== key)
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedKeys.length === 0) return;
    if (!window.confirm(`ยืนยันลบ ${selectedKeys.length} รายการที่เลือก?`)) return;

    try {
      setDeletingSelected(true);
      const selectedSet = new Set(selectedKeys);
      const selectedPending = pendingItems.filter((item) => selectedSet.has(itemKey(item)));
      const selectedSaved = savedItems.filter((item) => selectedSet.has(itemKey(item)));

      selectedPending.forEach((item) => sessionStorage.removeItem(`excelPreview:${item.tempId}`));
      setPendingItems((prev) => prev.filter((item) => !selectedSet.has(itemKey(item))));

      if (selectedSaved.length) {
        await Promise.all(selectedSaved.map((item) => deleteById(item.id)));
        await loadList();
      }

      setSelectedKeys([]);
    } catch (err) {
      console.error(err);
      window.alert(err.message || "ลบรายการที่เลือกไม่สำเร็จ");
    } finally {
      setDeletingSelected(false);
    }
  };

  const saveBatch = async (itemsToSave) => {
    const res = await fetch(`${API_BASE}/api/excel/save-all`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: itemsToSave }),
    });
    const data = await res.json();
    if (!res.ok || data.success === false) {
      throw new Error(data.message || "Save failed");
    }
    return data;
  };

  const saveItems = async (itemsToSave) => {
    const validItems = itemsToSave.filter((item) => !item.errors?.length);
    if (!validItems.length) {
      window.alert("ไม่มี pending item ที่พร้อมบันทึก");
      return;
    }

    try {
      setSaving(true);
      setCaptureStatus("");

      setCapturingImages(true);
      let savedCount = 0;
      let capturedCount = 0;
      const saveErrors = [];
      const captureErrors = [];

      for (let index = 0; index < validItems.length; index += 1) {
        const pendingItem = validItems[index];
        setCaptureStatus(`กำลังบันทึกและสร้างรูปประกาศ ${index + 1}/${validItems.length}`);

        try {
          const data = await saveBatch([pendingItem]);
          const savedItem = data.savedItems?.[0] ? { ...data.savedItems[0], status: data.savedItems[0].status || "saved" } : null;

          if (!savedItem || data.savedCount < 1) {
            throw new Error(data.errors?.[0]?.message || "Save failed");
          }

          savedCount += 1;
          setPendingItems((prev) => prev.filter((item) => item.tempId !== pendingItem.tempId));
          setSavedItems((prev) => [savedItem, ...prev]);
          setSelectedKeys((prev) => prev.filter((key) => key !== itemKey(pendingItem)));

          try {
            await captureImportedItemAsAnnouncement(savedItem);
            capturedCount += 1;
          } catch (err) {
            console.error(err);
            captureErrors.push({ item: savedItem, message: err.message || "Capture failed" });
          }
        } catch (err) {
          console.error(err);
          saveErrors.push({ item: pendingItem, message: err.message || "Save failed" });
        }
      }

      setUploadResult({
        success: saveErrors.length === 0 && captureErrors.length === 0,
        message: saveErrors.length || captureErrors.length ? "บันทึกสำเร็จบางส่วน" : "บันทึกสำเร็จ",
        totalRows: validItems.length,
        successCount: savedCount,
        errorCount: saveErrors.length,
        errors: saveErrors,
      });

      setCaptureStatus(
        saveErrors.length || captureErrors.length
          ? `บันทึกสำเร็จ ${savedCount}/${validItems.length}, สร้างรูปสำเร็จ ${capturedCount}/${savedCount}, ผิดพลาด ${saveErrors.length + captureErrors.length}`
          : `บันทึกและสร้างรูปประกาศสำเร็จ ${capturedCount}/${validItems.length} รายการ`
      );

      await loadList();
      window.alert(
        `Saved ${savedCount}/${validItems.length} item(s). สร้างรูปประกาศสำเร็จ ${capturedCount}/${savedCount}` +
          (saveErrors.length ? `, save failed ${saveErrors.length}` : "") +
          (captureErrors.length ? `, capture failed ${captureErrors.length}` : "")
      );
    } catch (err) {
      console.error(err);
      window.alert(err.message || "บันทึกไม่สำเร็จ");
    } finally {
      setSaving(false);
      setCapturingImages(false);
    }
  };

  const handleSaveSelected = () => {
    const selectedSet = new Set(selectedKeys);
    saveItems(pendingItems.filter((item) => selectedSet.has(itemKey(item))));
  };

  const handleSaveAll = () => {
    saveItems(saveablePendingItems);
  };

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="excel-upload-form">
      <div className="euf-section">
        <div className="euf-list-header">
          <div className="euf-title euf-title-inline">Upload Excel หลายประกาศ</div>
          <button className="euf-btn-sm euf-btn-info" type="button" onClick={handleDownloadTemplate}>
            Download Excel Template
          </button>
        </div>

        <div className="euf-upload-box">
          <div className="euf-field">
            <label className="euf-label">ไฟล์ Excel (.xlsx)</label>
            <input
              id="excel-file-input"
              type="file"
              accept=".xlsx"
              onChange={handleFileSelect}
              className="euf-file-input"
            />
            {file && (
              <div className="euf-file-name">
                {file.name} ({(file.size / 1024).toFixed(2)} KB)
              </div>
            )}
          </div>

          <div className="euf-hint">
            ดาวน์โหลด template จากระบบเท่านั้น: 1 worksheet = 1 form, Row 1 เป็นชื่อฟอร์ม,
            Row 2 เป็น header ที่ห้ามแก้ และกรอกข้อมูลตั้งแต่ Row 3 เป็นต้นไป
          </div>

          <button
            className="euf-btn euf-btn-primary"
            onClick={handlePreviewUpload}
            disabled={!file || uploading}
          >
            {uploading ? "กำลังอ่านไฟล์..." : "Upload Preview"}
          </button>

          {uploadResult && (
            <div className={`euf-msg ${uploadResult.success ? "euf-msg-ok" : "euf-msg-error"}`}>
              {uploadResult.message && <div>{uploadResult.message}</div>}
              <div>
                ทั้งหมด {uploadResult.totalRows || 0} รายการ, พร้อมบันทึก{" "}
                {uploadResult.pendingCount ?? uploadResult.successCount ?? 0}, ผิดพลาด {uploadResult.errorCount || 0}
              </div>
              {uploadResult.sheets?.length > 0 && (
                <div className="euf-sheet-summary">
                  {uploadResult.sheets.map((sheet) => (
                    <span key={sheet.sheetName}>
                      {sheet.sheetName}: {sheet.successCount} ok / {sheet.errorCount} error
                    </span>
                  ))}
                </div>
              )}
              {uploadResult.errors?.length > 0 && (
                <ul>
                  {uploadResult.errors.map((err, index) => (
                    <li key={index}>{err.message || String(err)}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {captureStatus && (
            <div className={`euf-msg ${capturingImages ? "euf-msg-ok" : ""}`}>
              {captureStatus}
            </div>
          )}
        </div>
      </div>

      <div className="euf-section">
        <div className="euf-list-header">
          <div className="euf-title euf-title-inline">รายการที่ import ได้</div>
          <div className="euf-toolbar">
            <button
              className="euf-btn-sm euf-btn-success"
              onClick={handleSaveSelected}
              disabled={saving || selectedKeys.length === 0}
            >
              Save selected
            </button>
            <button
              className="euf-btn-sm euf-btn-success"
              onClick={handleSaveAll}
              disabled={saving || saveablePendingItems.length === 0}
            >
              Save All ({saveablePendingItems.length})
            </button>
            <button
              className="euf-btn-sm euf-btn-danger euf-delete-selected"
              onClick={handleDeleteSelected}
              disabled={selectedKeys.length === 0 || deletingSelected}
            >
              {deletingSelected ? "Deleting..." : `Delete selected (${selectedKeys.length})`}
            </button>
          </div>
        </div>

        {loadingList ? (
          <div className="euf-empty">กำลังโหลด...</div>
        ) : items.length === 0 ? (
          <div className="euf-empty">ยังไม่มีรายการ</div>
        ) : (
          <>
          <PageSummary paging={paging} />
          <div className="euf-table">
            <table>
              <thead>
                <tr>
                  <th className="euf-check-col">
                    <input
                      type="checkbox"
                      checked={paging.shown.length > 0 && paging.shown.every((it) => selectedKeys.includes(itemKey(it)))}
                      onChange={(e) => toggleSelectAll(e.target.checked)}
                      aria-label="Select all records on this page"
                    />
                  </th>
                  <th>ID</th>
                  <th>Sheet</th>
                  <th>Form</th>
                  <th>Company</th>
                  <th>Issue date</th>
                  <th>Meeting date</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paging.shown.map((item) => (
                  <tr key={itemKey(item)} className={item.status === "error" ? "euf-row-error" : ""}>
                    <td className="euf-check-col">
                      <input
                        type="checkbox"
                        checked={selectedKeys.includes(itemKey(item))}
                        onChange={(e) => toggleSelectItem(item, e.target.checked)}
                        aria-label={`Select imported record ${item.tempId || item.id}`}
                      />
                    </td>
                    <td>{item.id ? `#${item.id}` : item.tempId?.slice(0, 8)}</td>
                    <td>{item.sheetName || item.formType || "-"}</td>
                    <td>{item.displayName || item.formTitle || "-"}</td>
                    <td>{item.company || item.formData?.company || "-"}</td>
                    <td>{displayDate(item.issueDate || item.formData?.issueDate)}</td>
                    <td>{displayDate(item.meetingDate || item.formData?.meetingDate)}</td>
                    <td>
                      {item.status || "saved"}
                      {item.errors?.length > 0 && (
                        <div className="euf-row-errors">{item.errors.join(" | ")}</div>
                      )}
                    </td>
                    <td className="euf-actions">
                      <button className="euf-btn-sm euf-btn-info" onClick={() => openTemplate(item)}>
                        View
                      </button>
                      <button className="euf-btn-sm euf-btn-danger" onClick={() => handleDelete(item)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PageNav paging={paging} />
          </>
        )}
      </div>
    </div>
  );
}

export default ExcelUploadForm;
